import {
  assignReferrer,
  createEvoClient,
  getProfile,
  getUsersPublicDomainConfiguration,
  verifyReferralCode,
} from "@jti/evo-client";
import { getConfig } from "@jti/fe-common";

import { getUser } from "../../../utils";
import type {
  GetReferralStatsResponseType,
  ReferralApiContract,
  ReferralCodeSubmissionSource,
  ReferralCustomerType,
} from "../ReferralApi.types";

export class ReferralApiEvo implements ReferralApiContract {
  private readonly client;
  constructor(baseUrl: string, tenantToken: string, recaptchaId: string) {
    const { ciam_token } = getUser();
    this.client = createEvoClient({
      baseUrl,
      tenantToken,
      recaptchaId,
      ciamToken: ciam_token,
      throwOnError: true,
    });
  }

  static init(): ReferralApiEvo {
    const { EVO_DOMAIN, EVO_TENANT, "X-RECAPTCHA-ID": recaptchaId } = getConfig();
    return new ReferralApiEvo(EVO_DOMAIN, EVO_TENANT, recaptchaId);
  }

  async getCustomer(uuid: string, _: string, skipCache: boolean = false): Promise<ReferralCustomerType> {
    const EMPTY_CUSTOMER: ReferralCustomerType = {
      id: "",
      uuid: "",
      code: "",
      parent_code: "",
      tenant_code: "",
      has_first_purchase: null,
    };

    const { data } = await getProfile({
      client: this.client,
      path: {
        uuid,
      },
      headers: {
        "X-SKIP-SW-CACHE": skipCache,
      },
    });

    if (!data?.data?.consumer) {
      return EMPTY_CUSTOMER;
    }

    const { consumer } = data.data;

    return {
      id: "",
      tenant_code: "",
      uuid: consumer?.uuid,
      code: consumer?.referral_code,
      parent_code: consumer?.parent_code,
      has_first_purchase: consumer?.has_first_referrer_purchase,
    } as ReferralCustomerType;
  }

  async getStatus(): Promise<boolean> {
    const { data } = await getUsersPublicDomainConfiguration({
      client: this.client,
    });

    return Boolean(data?.referrals?.enabled);
  }

  async verifyCode(code: string): Promise<boolean> {
    const UNPROCESSABLE_ENTITY_STATUS = 422;
    const { error, response } = await verifyReferralCode({
      throwOnError: false,
      client: this.client,
      path: {
        code,
      },
    });

    if (!response.ok) {
      if (response.status === UNPROCESSABLE_ENTITY_STATUS) {
        return false;
      }
      throw error;
    }

    return true;
  }

  async getReferralStats(uuid: string): Promise<GetReferralStatsResponseType> {
    const EMPTY_STATS: GetReferralStatsResponseType = {
      referees_count: 0,
      completed_first_order_count: 0,
      earned_points: 0,
    };

    try {
      const { data } = await getProfile({
        client: this.client,
        path: {
          uuid,
        },
      });

      if (!data?.data?.referrer_statistics) {
        return EMPTY_STATS;
      }

      const { referrer_statistics } = data.data;

      return {
        completed_first_order_count:
          referrer_statistics.completed_first_order_count ?? EMPTY_STATS.completed_first_order_count,
        earned_points: referrer_statistics.earned_points ?? EMPTY_STATS.earned_points,
        referees_count: referrer_statistics.referrals_count ?? EMPTY_STATS.referees_count,
      };
    } catch {
      return EMPTY_STATS;
    }
  }

  async updateReferralCode(
    uuid: string,
    referrer_code: string,
    _: string,
    source?: ReferralCodeSubmissionSource
  ): Promise<boolean> {
    await assignReferrer({
      client: this.client,
      path: {
        uuid,
      },
      body: {
        referrer_code,
        source: source ?? undefined,
      },
    });
    return true;
  }
}
